import React, { Suspense, useEffect } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { Switch, Route, Redirect, matchPath } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { Backdrop } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { motion } from "framer-motion";
import { ClimbingBoxLoader } from "react-spinners";

import * as serviceWorker from "./serviceWorker";
import Spinner from "./components/Spinner";
import { setWebsocketConnection } from "./reducers/WebsocketReducer";
import { setUserAuthenticated, setUserData } from "./reducers/UserReducer";
import { notify, notifyPanel } from "./reducers/NotifierReducer";
import { getAccessPermission, getUserAccessPermission } from "./reducers/PermissionsReducer";

// Layout Blueprints
import { MinimalLayout, MainLayout } from "./layout-blueprints";

// Pages
import PageLogin from "./pages/PageLogin";
import Page404 from "./pages/Page404";
import PageResetPassword from "./pages/PageResetPassword";
import routeDefinitions, { assetSentinalDefaultUrl, defaultUrl } from "./routeDefinitions";
import client from "./feathersClient";
import { routeToModuleMap } from "./utils/enums";
import { checkAPIVersion } from "./utils/functions";
import { showSpinner, hideSpinner } from "./reducers/UiReducer";

const employeePaths = ['/employee', '/employee/edit/:id'];

const checkNecessaryPermissions = (pathname, userAgency, amsPermission, assetSentinelPermission, searchParams) => {
	const exactMatch = routeToModuleMap[pathname];
	const matchedKey = exactMatch
		? pathname
		: Object.keys(routeToModuleMap).find((routePath) =>
			matchPath(pathname, { path: routePath, exact: true, strict: false }),
		);
	const moduleDef = routeToModuleMap[matchedKey];

	if (
		employeePaths.includes(matchedKey)  &&
		moduleDef &&
		!amsPermission &&
		assetSentinelPermission
	) {
		if (matchedKey === employeePaths[1]) {
			if (searchParams.includes("readOnly=true")) return { allowed: true };
			else return { allowed: false };
		}
		return { allowed: true };
	}

	if (!moduleDef) {
		return { allowed: true };
	}

	if (moduleDef?.isAmsRoute === true && !amsPermission) {
		return { allowed: false };
	}

	if (moduleDef?.isAmsRoute === false && !assetSentinelPermission) {
		return { allowed: false };
	}

	const modulePermissions = getAccessPermission(
		moduleDef.area,
		moduleDef.key,
		userAgency,
	);

	if (!modulePermissions) {
		return { allowed: null };
	}

	if (!modulePermissions.Read) {
		return { allowed: false };
	}

	return { allowed: true };
};

const ProtectedRoute = ({
	component: Component,
	userAgency,
	isAuthenticated,
	...rest
}) => {
	const dispatch = useDispatch();
	const user = useSelector((state) => state.user);
	const { appVersion, apiVersion, appVersionInfo } = useSelector(
		(state) => state.appInfo,
	);
	const amsPermission =
		user?.userData?.user?.Permissions && getUserAccessPermission("Modules", "AMS", "any");
	
	const assetSentinelPermission =
		user?.userData?.user?.Permissions && getUserAccessPermission("Modules", "Asset Sentinel");

	const verifyAMSVersion = () => {
		if (!amsPermission && !assetSentinelPermission) {
			dispatch(
				notify(
					"User does not have permission to access this application. Please try again.",
					"error",
				),
			);
			dispatch(setUserAuthenticated(false));
			return;
		}

		if (appVersion && apiVersion && appVersionInfo) {
			if (
				!(
					appVersionInfo?.app_version === appVersion &&
					checkAPIVersion(apiVersion, appVersionInfo?.api_version)
				)
			) {
				dispatch(
					notify(
						"Current app version is not supported. Please try again.",
						"error",
					),
				);
				dispatch(setUserAuthenticated(false));
			}
		}
	};

	useEffect(() => {
		verifyAMSVersion();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appVersion, apiVersion, appVersionInfo]);

	return (
		<Route
			{...rest}
			render={(props) => {
				if (!isAuthenticated) {
					return (
						<Redirect
							to={{
								pathname: "/Login",
								state: { from: props.location },
							}}
						/>
					);
				}

				const { allowed } = checkNecessaryPermissions(
					props.location?.pathname,
					userAgency,
					amsPermission,
					assetSentinelPermission,
					props.location?.search
				);

				if (allowed === null) {
					return (
						<Backdrop open>
							<CircularProgress color="primary" />
						</Backdrop>
					);
				}

				if (allowed === false) {
					dispatch(
						notify(
							"You do not have permission to access this page",
							"warning",
						),
					);
					return (
						<Redirect
							to={{
								pathname: "/Main",
								state: { from: props.location },
							}}
						/>
					);
				}

				return <Component {...props} />;
			}}
		/>
	);
};

const LoginRoute = ({ component: Component, isAuthenticated, ...rest }) => (
	<Route
		{...rest}
		render={(props) =>
			isAuthenticated === true ? (
				<Redirect
					to={{
						pathname: getUserAccessPermission("Modules", "AMS", "any") ? defaultUrl : assetSentinalDefaultUrl,
						state: { from: props.location },
					}}
				/>
			) : (
				<Component {...props} />
			)
		}
	/>
);

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: "#fff",
	},
}));

const Routes = (props) => {
	const {
		isAuthenticated,
		setWebsocketConnection,
		setUserAuthenticated,
		setUserData,
		setLoggedInUserData,
		configLoaded,
		circularLoading,
		globalCircularLoading,
		userData,
    	websocket,
	} = props;

	const userAgency = useSelector((state) => state.user.userAgency);
	const classes = useStyles();
	const dispatch = useDispatch();

	React.useEffect(() => {
		// handle service worker updates
		serviceWorker.register({
			onSuccess: () => console.log("service worker registered!"),
			onUpdate: () => {
				sessionStorage.setItem('pending_update', 'true');
			},
		});
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (!isAuthenticated) return;
		const hasUpdate = sessionStorage.getItem('pending_update');
		if (hasUpdate !== 'true') return;
		navigator.serviceWorker.getRegistration().then((reg) => {
			if (!reg) return;
			const waitingServiceWorker = reg.waiting;
			if (!waitingServiceWorker) return;
			waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
			waitingServiceWorker.onstatechange = (event) => {
				if (event.target.state === 'activated') {
					sessionStorage.removeItem('pending_update');
					window.location.reload();
				}
			};
		});
	}, [isAuthenticated]);

	React.useEffect(() => {
		async function establishConnection() {
			try {
				const user = await client.reAuthenticate({ type: "ams" });
				setWebsocketConnection(client);
				setUserData(user);
				setUserAuthenticated(true);
			} catch (error) {
				if (error.code === 401) {
					setUserAuthenticated(false);
					setUserData(null);
					setWebsocketConnection(client);
				}
				setWebsocketConnection(client);
			}
		}
		establishConnection();
		// eslint-disable-next-line
	}, [setWebsocketConnection, setUserAuthenticated, setLoggedInUserData]);

	const refreshToken = async () => {
		try {
			props.showSpinner();
			const currentToken = localStorage.getItem('auth');
			const response = await websocket.service('authentication').create({
				strategy: 'jwt',
				accessToken: currentToken,
				isRenewToken: true,
				AppName: 'AMS', 
			});
			localStorage.setItem('auth', response.accessToken);
			websocket.set('authentication', response);
			setUserData(response);
			setUserAuthenticated(true);
			dispatch(notify('Session extended!', 'success'));
		} catch (error) {
			console.error('Refresh Failed', error);
			setUserAuthenticated(false);
		} finally {
			props.hideSpinner();
		}
	};

  const showWarning = (expiryTime) => {
    const expiryTimeText = new Date(expiryTime)
      .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
    props.notifyPanel(
      {
        title: 'Session Expiring Soon!',
        message: `Your session will expire at ${expiryTimeText}. Click CONTINUE to stay signed in.`,
        position: 'tc',
        level: 'warning',
        autoDismiss: props.sessionWarningMinutes * 60,
        action: {
          label: 'CONTINUE',
          callback: refreshToken,
        },
      },
      'warning'
    );
  };

  React.useEffect(() => {    
    if (!isAuthenticated || !userData || !props.sessionWarningEnabled) return;
		const payload = userData?.authentication?.payload || userData?.payload;  
		const exp = payload?.exp;
		if (!exp) return;  
		const expiryTime = exp * 1000;
		const warningSeconds = Number(props.sessionWarningMinutes) * 60;
		const now = Date.now();
		const delay = expiryTime - now - (warningSeconds * 1000);

		if (delay <= 0) {
			if (now < expiryTime) {
			showWarning(expiryTime);
			} 
			return;
		}
		const timer = setTimeout(() => {
			showWarning(expiryTime);
		}, delay);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated, userData, props.sessionWarningEnabled]);

	const pageVariants = {
		initial: { opacity: 0, scale: 0.99, width: "100%", height: "100%" },
		in: { opacity: 1, scale: 1 },
		out: { opacity: 0, scale: 1.01 },
	};

	const pageTransition = {
		type: "tween",
		ease: "anticipate",
		duration: 0.4,
	};

	const SuspenseLoading = () => (
		<div className="d-flex align-items-center flex-column vh-100 justify-content-center text-center py-3">
			<div className="d-flex align-items-center flex-column px-4">
				<ClimbingBoxLoader color={"#0153a3"} loading={true} />
			</div>
			<div className="text-muted font-size-xl text-center pt-3">
				Please wait while we load the view...
			</div>
		</div>
	);

	const loadingPage = () => <Spinner />;

	return (
		<Suspense fallback={<SuspenseLoading />}>
			<Switch>
				<Redirect exact from="/" to="/Login" />
				<Route path={["/Login"]}>
					<MinimalLayout>
						<motion.div
							initial="initial"
							animate="in"
							exit="out"
							variants={pageVariants}
							transition={pageTransition}
						>
							<LoginRoute
								path="/Login"
								component={PageLogin}
								isAuthenticated={isAuthenticated}
							/>
						</motion.div>
					</MinimalLayout>
				</Route>

				<Route path={["/Reset-password"]}>
					<MinimalLayout>
						<PageResetPassword isAuthenticated={isAuthenticated} />
					</MinimalLayout>
				</Route>

				{routeDefinitions.map((route, key) => {
					const Layout = route.layout;
					return (
						<Route exact path={[route.url]} key={key}>
							<>
								<Backdrop
									className={classes.backdrop}
									open={
										globalCircularLoading || circularLoading
									}
								>
									<CircularProgress color="inherit" />
								</Backdrop>
								<Layout>
									<motion.div
										initial="initial"
										animate="in"
										exit="out"
										variants={pageVariants}
										transition={pageTransition}
									>
										<ProtectedRoute
											path={route.url}
											component={
												configLoaded
													? route.component
													: loadingPage
											}
											isAuthenticated={isAuthenticated}
											userAgency={userAgency}
										/>
									</motion.div>
								</Layout>
							</>
						</Route>
					);
				})}

				<Route>
					<MainLayout>
						<motion.div
							initial="initial"
							animate="in"
							exit="out"
							variants={pageVariants}
							transition={pageTransition}
						>
							<ProtectedRoute
								component={Page404}
								isAuthenticated={isAuthenticated}
							/>
						</motion.div>
					</MainLayout>
				</Route>
			</Switch>
		</Suspense>
	);
};

const mapStateToProps = (state) => ({
	network: state.offline,
	isAuthenticated: state.user.isAuthenticated,
	user: state.user,
	configLoaded: state.config.loaded,
	circularLoading: state.ui.circularLoading,
	globalCircularLoading: state.ui.globalCircularLoading,
	userData: state.user.userData,
	websocket: state.websocket.websocket,
	sessionWarningEnabled: state.config.sessionWarningEnabled,
	sessionWarningMinutes: state.config.sessionWarningMinutes,
});

export default connect(mapStateToProps, {
	setWebsocketConnection,
	setUserAuthenticated,
	setUserData,
	notifyPanel,
	showSpinner, 
  	hideSpinner,
})(Routes);
